import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { v4 } from "uuid";
import {
  Check,
  EllipsisVertical,
  Info,
  Loader,
  Pencil,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { DialogDescription, DialogFooter } from "@/shared/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { DialogTooltipButton } from "@/shared/components/dialog/dialog-tooltip-button";
import { cn } from "@/shared/lib/utils";
import { FloatNumberInput } from "@/shared/components/domain/qpm/inputs/number-input";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { useDebounce } from "@/shared/lib/debounce/debounce";
import { usePersistenceContext } from "@/shared/lib/persistence-state/context";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";
import type {
  MonitorAdditionalDTO,
  MonitorAdditionalLibraryItemDTO,
} from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { normalizeMonitorAdditionalConfig } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import type { MonitorAdditionalReq } from "@/services/backend/qpm/monitors/types/monitor-commands";
import type { MonitorType } from "@/services/backend/qpm/types/enums";
import {
  useQpmMonitorAdditionalsLibraryQuery,
  useQpmMonitorReplaceAdditionalsMutation,
} from "@/services/backend/qpm/monitors/api/monitor-api";
import { useQpmCatalogItemSetAdditionalsMutation } from "@/services/backend/qpm/catalog/api/catalog-api";
import {
  EditableRentingFeeRow,
  hasInvalidRentingFeeRows,
  sortedRentingFeeRows,
} from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/editable-renting-fees-table";
import { MonitorAdditionalEditForm } from "@/routes/base-data/processes/qpm/_components/monitor-additional-edit-form";
import { MonitorAdditionalImageSlot } from "@/routes/base-data/processes/qpm/_components/monitor-additional-image-slot";
import { RentingFeesInfoCell } from "@/routes/base-data/processes/qpm/_components/renting-fees-info-cell";

type EditorMode =
  | { kind: "base"; monitorId: string }
  | { kind: "catalog"; catalogItemId: string };

type Props = {
  mode: EditorMode;
  monitorName: string;
  monitorType: MonitorType;
  additionals: MonitorAdditionalDTO[];
  libraryItems?: MonitorAdditionalLibraryItemDTO[];
};

const baseDataTooltipContentClassName =
  "bg-background text-foreground max-w-sm border text-left text-xs [&>svg]:bg-background [&>svg]:fill-background";

type AdditionalEditorValue = MonitorAdditionalReq;
type EditorSelection =
  | { mode: "create"; additional: AdditionalEditorValue }
  | { mode: "library"; additional: AdditionalEditorValue }
  | { mode: "assigned"; additionalId: string };

function toEditableAdditionals(
  additionals: MonitorAdditionalDTO[],
  libraryItems: MonitorAdditionalLibraryItemDTO[] = [],
): AdditionalEditorValue[] {
  const libraryById = new Map(libraryItems.map((item) => [item.id, item]));
  const libraryByKey = new Map(
    libraryItems
      .map((item) => [getAdditionalDuplicateKey(item), item] as const)
      .filter(([key]) => key),
  );
  return normalizeDraftAdditionals(
    additionals.map((additional) => {
      const libraryAdditional =
        libraryById.get(additional.id) ??
        libraryByKey.get(getAdditionalDuplicateKey(additional));
      return toEditableAdditional(additional, libraryAdditional);
    }),
  );
}

function normalizeDisplayedAdditionals(
  additionals: MonitorAdditionalDTO[],
  libraryItems: MonitorAdditionalLibraryItemDTO[] = [],
): MonitorAdditionalDTO[] {
  const libraryById = new Map(libraryItems.map((item) => [item.id, item]));
  const libraryByKey = new Map(
    libraryItems
      .map((item) => [getAdditionalDuplicateKey(item), item] as const)
      .filter(([key]) => key),
  );
  const seenKeys = new Set<string>();
  return [...additionals]
    .map((additional) => {
      const libraryAdditional =
        libraryById.get(additional.id) ??
        libraryByKey.get(getAdditionalDuplicateKey(additional));
      return toDisplayedAdditional(additional, libraryAdditional);
    })
    .sort(
      (left, right) =>
        compareAdditionalTimestamps(left.createdAt, right.createdAt) ||
        left.name.localeCompare(right.name, undefined, {
          sensitivity: "base",
        }),
    )
    .filter((additional) => {
      const key = getAdditionalDuplicateKey(additional);
      if (!key) {
        return true;
      }
      if (seenKeys.has(key)) {
        return false;
      }
      seenKeys.add(key);
      return true;
    });
}

function toEditableAdditional(
  additional: MonitorAdditionalDTO,
  libraryAdditional?: MonitorAdditionalLibraryItemDTO,
): AdditionalEditorValue {
  return {
    id: libraryAdditional?.id ?? additional.id,
    name: libraryAdditional?.name ?? additional.name ?? "",
    description: libraryAdditional?.description ?? additional.description ?? "",
    basePrice: libraryAdditional?.basePrice ?? additional.basePrice ?? 0,
    rentingFees: libraryAdditional?.rentingFees ?? additional.rentingFees ?? {},
    config: normalizeMonitorAdditionalConfig(
      libraryAdditional?.config ?? additional.config,
    ),
    createdAt: libraryAdditional?.createdAt ?? additional.createdAt,
    updatedAt: libraryAdditional?.updatedAt ?? additional.updatedAt,
  };
}

function toDisplayedAdditional(
  additional: MonitorAdditionalDTO,
  libraryAdditional?: MonitorAdditionalLibraryItemDTO,
): MonitorAdditionalDTO {
  return {
    ...additional,
    id: libraryAdditional?.id ?? additional.id,
    name: libraryAdditional?.name ?? additional.name ?? "",
    description: libraryAdditional?.description ?? additional.description ?? "",
    basePrice: libraryAdditional?.basePrice ?? additional.basePrice ?? 0,
    rentingFees: libraryAdditional?.rentingFees ?? additional.rentingFees ?? {},
    config: normalizeMonitorAdditionalConfig(
      libraryAdditional?.config ?? additional.config,
    ),
    createdAt: libraryAdditional?.createdAt ?? additional.createdAt,
    updatedAt: libraryAdditional?.updatedAt ?? additional.updatedAt,
    image: libraryAdditional?.image ?? additional.image,
  };
}

function normalizeDraftAdditionals(
  additionals: AdditionalEditorValue[],
): AdditionalEditorValue[] {
  const seenKeys = new Set<string>();
  return [...additionals]
    .sort(
      (left, right) =>
        compareAdditionalTimestamps(left.createdAt, right.createdAt) ||
        left.name.localeCompare(right.name, undefined, {
          sensitivity: "base",
        }),
    )
    .filter((additional) => {
      const key = getAdditionalDuplicateKey(additional);
      if (!key) {
        return true;
      }
      if (seenKeys.has(key)) {
        return false;
      }
      seenKeys.add(key);
      return true;
    });
}

function getAdditionalDuplicateKey(additional: { name?: string }): string {
  const name = (additional.name ?? "").trim().toLocaleLowerCase();
  if (!name) {
    return "";
  }
  return `name:${name}`;
}

function compareAdditionalTimestamps(left?: string, right?: string): number {
  const leftTime = left ? Date.parse(left) : Number.MAX_SAFE_INTEGER;
  const rightTime = right ? Date.parse(right) : Number.MAX_SAFE_INTEGER;
  if (leftTime === rightTime) {
    return 0;
  }
  return leftTime < rightTime ? -1 : 1;
}

function cloneLibraryAdditional(
  additional: MonitorAdditionalLibraryItemDTO,
): AdditionalEditorValue {
  return {
    id: additional.id,
    name: additional.name ?? "",
    description: additional.description ?? "",
    basePrice: additional.basePrice ?? 0,
    rentingFees: additional.rentingFees ?? {},
    config: normalizeMonitorAdditionalConfig(additional.config),
    createdAt: additional.createdAt,
    updatedAt: additional.updatedAt,
  };
}

function createNewAdditional(): AdditionalEditorValue {
  const now = new Date().toISOString();
  return {
    id: v4(),
    name: "",
    description: "",
    basePrice: 0,
    rentingFees: {},
    config: normalizeMonitorAdditionalConfig(),
    createdAt: now,
    updatedAt: now,
  };
}

function sanitizeAdditionalForMonitorType(
  additional: AdditionalEditorValue,
  monitorType: MonitorType,
): AdditionalEditorValue {
  if (monitorType === "IOT_MONITOR") {
    return {
      ...additional,
      config: normalizeMonitorAdditionalConfig(additional.config),
    };
  }
  return {
    ...additional,
    rentingFees: {},
    config: normalizeMonitorAdditionalConfig(additional.config),
  };
}

function isAdditionalEmpty(additional: AdditionalEditorValue): boolean {
  return (
    additional.name.trim() === "" &&
    additional.description.trim() === "" &&
    additional.basePrice === 0
  );
}

function rentingFeesToRows(
  rentingFees: Record<number, number>,
): EditableRentingFeeRow[] {
  return sortedRentingFeeRows(
    Object.entries(rentingFees)
      .map(([durationMonths, monthly]) => ({
        durationMonths: Number(durationMonths),
        monthly,
      }))
      .filter((fee) => fee.durationMonths > 0),
  );
}

function AdditionalSummaryPriceInput({
  additionalId,
  price,
  disabled,
  onPriceChange,
}: {
  additionalId: string;
  price: number;
  disabled: boolean;
  onPriceChange: (additionalId: string, price: number) => void;
}) {
  const { setPersistenceState } = usePersistenceContext();
  const [localPrice, setLocalPrice] = useState(price);
  const debouncedPrice = useDebounce(localPrice, 350);
  const didHydrate = useRef(false);
  const hasUserEdited = useRef(false);

  useEffect(() => {
    hasUserEdited.current = false;
    setLocalPrice(price);
  }, [price]);

  useEffect(() => {
    if (!didHydrate.current) {
      didHydrate.current = true;
      return;
    }
    if (!hasUserEdited.current) {
      return;
    }
    if (debouncedPrice === price) {
      hasUserEdited.current = false;
      return;
    }
    setPersistenceState(PersistenceState.SAVING);
    onPriceChange(additionalId, debouncedPrice);
  }, [additionalId, debouncedPrice, onPriceChange, price, setPersistenceState]);

  return (
    <FloatNumberInput
      className="w-24 justify-self-end"
      value={localPrice}
      fractions={2}
      disabled={disabled}
      onChange={(nextPrice) => {
        hasUserEdited.current = true;
        setLocalPrice(nextPrice);
      }}
    />
  );
}

function AdditionalSummary({
  additionals,
  libraryItems,
  disabled,
  onEdit,
  onDelete,
  onPriceChange,
}: {
  additionals: MonitorAdditionalDTO[];
  libraryItems: MonitorAdditionalLibraryItemDTO[];
  disabled: boolean;
  onEdit: (additionalId: string) => void;
  onDelete: (additionalId: string) => void;
  onPriceChange: (additionalId: string, price: number) => void;
}) {
  const runMenuAction = (action: () => void) => {
    window.setTimeout(action, 0);
  };
  const visibleAdditionals = normalizeDisplayedAdditionals(
    additionals,
    libraryItems,
  );

  if (visibleAdditionals.length === 0) {
    return (
      <div className="text-muted-foreground rounded-md border border-dashed px-4 py-3 text-sm">
        {t("Keine Zusätze vorhanden")}
      </div>
    );
  }

  return (
    <div className="divide-y rounded-md border">
      {visibleAdditionals.map((additional) => (
        <div
          key={additional.id}
          className="flex items-start justify-between gap-4 px-4 py-3"
        >
          <div className="min-w-0 space-y-1">
            <div className="font-medium">
              {additional.name || t("Unbenannter Zusatz")}
            </div>
            <div className="text-muted-foreground text-sm">
              {additional.description || t("Keine Beschreibung")}
            </div>
          </div>
          <div className="flex shrink-0 items-end gap-3">
            <div className="flex flex-col items-end gap-1">
              <span className="text-muted-foreground text-xs font-medium">
                {t("Preis")}
              </span>
              <AdditionalSummaryPriceInput
                additionalId={additional.id}
                price={additional.basePrice}
                disabled={disabled}
                onPriceChange={onPriceChange}
              />
            </div>
            <div className="flex flex-col items-center gap-1">
              <span className="text-muted-foreground text-xs font-medium">
                {t("Miete")}
              </span>
              <RentingFeesInfoCell
                rows={rentingFeesToRows(additional.rentingFees ?? {})}
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={disabled}>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={t("Aktionen")}
                >
                  <EllipsisVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t("Aktionen")}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onSelect={() => runMenuAction(() => onEdit(additional.id))}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    {t("Bearbeiten")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() =>
                      runMenuAction(() => onDelete(additional.id))
                    }
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("Entfernen")}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ))}
    </div>
  );
}

function AdditionalLibrarySearch({
  items,
  disabled,
  onSelect,
}: {
  items: MonitorAdditionalLibraryItemDTO[];
  disabled: boolean;
  onSelect: (item: MonitorAdditionalLibraryItemDTO) => void;
}) {
  return (
    <div className="bg-background max-w-2xl rounded-md border">
      <Command>
        <CommandInput placeholder={t("Vorhandene Zusätze durchsuchen")} />
        <CommandList className="max-h-56">
          <CommandEmpty>{t("Keine Treffer")}</CommandEmpty>
          <CommandGroup>
            {items.map((item) => (
              <CommandItem
                key={`${item.id}-${item.sourceMonitorId}`}
                value={`${item.name} ${item.description} ${item.sourceMonitorName}`}
                disabled={disabled}
                onSelect={() => onSelect(item)}
                className="items-start gap-3 py-3"
              >
                <Search className="mt-0.5 h-4 w-4 shrink-0 opacity-50" />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {item.name || t("Ohne Name")}
                    </span>
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {item.sourceMonitorName || t("Unbekannter Monitor")}
                  </div>
                  {item.description ? (
                    <div className="text-muted-foreground text-xs">
                      {item.description}
                    </div>
                  ) : null}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );
}

function AdditionalDraftList({
  additionals,
  selectedId,
  disabled,
  onSelect,
  onDelete,
  onEditRentingFees,
}: {
  additionals: AdditionalEditorValue[];
  selectedId: string | null;
  disabled: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onEditRentingFees: (id: string) => void;
}) {
  const runMenuAction = (action: () => void) => {
    window.setTimeout(action, 0);
  };

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium">{t("Zugeordnete Zusätze")}</div>

      <div className="space-y-2">
        {additionals.length === 0 ? (
          <div className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
            {t("Keine Zusätze ausgewählt")}
          </div>
        ) : null}

        {additionals.map((additional) => (
          <div
            key={additional.id}
            className={cn(
              "flex w-full items-start justify-between gap-3 rounded-md px-3 py-2 text-left transition-colors",
              selectedId === additional.id ? "bg-primary/5" : "hover:bg-accent",
            )}
          >
            <Button
              type="button"
              variant="ghost"
              className="h-auto min-w-0 flex-1 justify-start space-y-1 px-0 py-0 text-left hover:bg-transparent"
              onClick={() => onSelect(additional.id)}
            >
              <div className="font-medium">
                {additional.name || t("Unbenannter Zusatz")}
              </div>
              <div className="text-muted-foreground text-xs">
                {additional.description || t("Keine Beschreibung")}
              </div>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={disabled}>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={t("Aktionen")}
                >
                  <EllipsisVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t("Aktionen")}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onSelect={() =>
                      runMenuAction(() => onSelect(additional.id))
                    }
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    {t("Bearbeiten")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() =>
                      runMenuAction(() => onEditRentingFees(additional.id))
                    }
                  >
                    <Info className="mr-2 h-4 w-4" />
                    {t("Mietpreise")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() =>
                      runMenuAction(() => onDelete(additional.id))
                    }
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("Entfernen")}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MonitorAdditionalsEditor({
  mode,
  monitorName,
  monitorType,
  additionals,
  libraryItems: providedLibraryItems,
}: Props) {
  const { setPersistenceState } = usePersistenceContext();
  const [isOpen, setIsOpen] = useState(false);
  const [draftAdditionals, setDraftAdditionals] = useState<
    AdditionalEditorValue[]
  >(() => toEditableAdditionals(additionals));
  const [closeOnSuccessfulSave, setCloseOnSuccessfulSave] = useState(false);
  const hasShownSaveSuccessToast = useRef(false);
  const [selection, setSelection] = useState<EditorSelection>(() => ({
    mode: "create",
    additional: createNewAdditional(),
  }));
  const draftAdditionalsRef = useRef(draftAdditionals);
  const selectionRef = useRef(selection);

  const updateDraftAdditionals = useCallback(
    (
      next:
        | AdditionalEditorValue[]
        | ((current: AdditionalEditorValue[]) => AdditionalEditorValue[]),
    ) => {
      const nextDrafts =
        typeof next === "function" ? next(draftAdditionalsRef.current) : next;
      draftAdditionalsRef.current = nextDrafts;
      setDraftAdditionals(nextDrafts);
    },
    [],
  );

  const updateSelection = useCallback((next: EditorSelection) => {
    selectionRef.current = next;
    setSelection(next);
  }, []);

  const { data: libraryData, isLoading: isLibraryLoading } =
    useQpmMonitorAdditionalsLibraryQuery(undefined, {
      skip: providedLibraryItems !== undefined,
    });
  const libraryItems = useMemo(
    () => providedLibraryItems ?? libraryData?.items ?? [],
    [libraryData?.items, providedLibraryItems],
  );

  const [replaceBaseAdditionals, replaceBaseState] =
    useQpmMonitorReplaceAdditionalsMutation();
  const [replaceCatalogAdditionals, replaceCatalogState] =
    useQpmCatalogItemSetAdditionalsMutation();

  const persistedAdditionalsById = useMemo(
    () => new Map(additionals.map((additional) => [additional.id, additional])),
    [additionals],
  );

  const resetDialogState = useCallback(() => {
    const nextDrafts = toEditableAdditionals(additionals, libraryItems);
    updateDraftAdditionals(nextDrafts);
    updateSelection({
      mode: "create",
      additional: createNewAdditional(),
    });
  }, [additionals, libraryItems, updateDraftAdditionals, updateSelection]);

  useEffect(() => {
    if (!isOpen) {
      resetDialogState();
    }
  }, [isOpen, resetDialogState]);

  const selectedAdditional = useMemo(() => {
    if (selection.mode === "assigned") {
      return draftAdditionals.find(
        (additional) => additional.id === selection.additionalId,
      );
    }
    return selection.additional;
  }, [draftAdditionals, selection]);

  const selectedAdditionalId =
    selection.mode === "assigned" ? selection.additionalId : null;
  const showLibrarySearch = selection.mode !== "assigned";
  const assignedAdditionalKeys = useMemo(() => {
    return new Set(
      draftAdditionals
        .map((additional) => getAdditionalDuplicateKey(additional))
        .filter(Boolean),
    );
  }, [draftAdditionals]);
  const availableLibraryItems = useMemo(() => {
    return libraryItems.filter(
      (item) =>
        item.monitorType === monitorType &&
        !assignedAdditionalKeys.has(getAdditionalDuplicateKey(item)),
    );
  }, [assignedAdditionalKeys, libraryItems, monitorType]);

  const saveState =
    mode.kind === "base" ? replaceBaseState : replaceCatalogState;
  const isSaving = saveState.isLoading;
  const resetSaveState =
    mode.kind === "base" ? replaceBaseState.reset : replaceCatalogState.reset;

  const selectedPersistedAdditional =
    selection.mode === "assigned" && selectedAdditional
      ? persistedAdditionalsById.get(selectedAdditional.id)
      : undefined;

  useEffect(() => {
    if (closeOnSuccessfulSave && saveState.isSuccess && isOpen) {
      if (!hasShownSaveSuccessToast.current) {
        toast.success(t("Material-Zusätze gespeichert"));
        hasShownSaveSuccessToast.current = true;
      }
      setIsOpen(false);
      setCloseOnSuccessfulSave(false);
    }
  }, [closeOnSuccessfulSave, isOpen, saveState.isSuccess]);

  useEffect(() => {
    if (saveState.isLoading) {
      setPersistenceState(PersistenceState.SAVING);
      return;
    }
    if (saveState.error) {
      setPersistenceState(PersistenceState.ERROR);
      return;
    }
    if (saveState.isSuccess) {
      setPersistenceState(PersistenceState.SAVED);
    }
  }, [
    saveState.error,
    saveState.isLoading,
    saveState.isSuccess,
    setPersistenceState,
  ]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      resetSaveState();
      setCloseOnSuccessfulSave(false);
      hasShownSaveSuccessToast.current = false;
    }
    setIsOpen(nextOpen);
  };

  const persistAdditionals = useCallback(
    (nextAdditionals: AdditionalEditorValue[]) => {
      const normalized = normalizeDraftAdditionals(nextAdditionals);
      if (mode.kind === "base") {
        replaceBaseAdditionals({
          id: mode.monitorId,
          additionals: normalized,
        });
        return;
      }
      replaceCatalogAdditionals({
        catalogItemId: mode.catalogItemId,
        additionals: normalized,
      });
    },
    [mode, replaceBaseAdditionals, replaceCatalogAdditionals],
  );

  const handleAddFromLibrary = (item: MonitorAdditionalLibraryItemDTO) => {
    if (assignedAdditionalKeys.has(getAdditionalDuplicateKey(item))) {
      return;
    }
    updateSelection({
      mode: "library",
      additional: cloneLibraryAdditional(item),
    });
  };

  const handleDeleteDraftAdditional = (additionalId: string) => {
    updateDraftAdditionals((current) => {
      const next = current.filter(
        (additional) => additional.id !== additionalId,
      );
      if (
        selection.mode === "assigned" &&
        selection.additionalId === additionalId
      ) {
        updateSelection({
          mode: "create",
          additional: createNewAdditional(),
        });
      }
      return next;
    });
  };

  const openEditorForAdditional = (additionalId: string) => {
    const nextDrafts = toEditableAdditionals(additionals, libraryItems);
    resetSaveState();
    setCloseOnSuccessfulSave(false);
    updateDraftAdditionals(nextDrafts);
    updateSelection({ mode: "assigned", additionalId });
    setIsOpen(true);
  };

  const deletePersistedAdditional = (additionalId: string) => {
    persistAdditionals(
      toEditableAdditionals(additionals, libraryItems).filter(
        (additional) => additional.id !== additionalId,
      ),
    );
  };

  const updatePersistedAdditionalPrice = useCallback(
    (additionalId: string, basePrice: number) => {
      persistAdditionals(
        toEditableAdditionals(additionals, libraryItems).map((additional) =>
          additional.id === additionalId
            ? { ...additional, basePrice, updatedAt: new Date().toISOString() }
            : additional,
        ),
      );
    },
    [additionals, libraryItems, persistAdditionals],
  );

  const handleChangeSelectedAdditional = useCallback(
    (patch: Partial<AdditionalEditorValue>) => {
      const currentSelection = selectionRef.current;
      if (
        currentSelection.mode === "create" ||
        currentSelection.mode === "library"
      ) {
        updateSelection({
          ...currentSelection,
          additional: {
            ...currentSelection.additional,
            ...patch,
            updatedAt: new Date().toISOString(),
          },
        });
        return;
      }
      updateDraftAdditionals((current) =>
        current.map((additional) =>
          additional.id === currentSelection.additionalId
            ? { ...additional, ...patch, updatedAt: new Date().toISOString() }
            : additional,
        ),
      );
    },
    [updateDraftAdditionals, updateSelection],
  );

  const primaryActionLabel =
    selection.mode === "library" ? t("Hinzufügen") : t("Speichern");

  const editorTitle =
    selection.mode === "library"
      ? t("Vorlage übernehmen")
      : selection.mode === "assigned"
        ? t("Ausgewählten Zusatz bearbeiten")
        : t("Neuen Zusatz erfassen");

  const editorDescription =
    selection.mode === "library"
      ? t(
          "Die Daten aus der ausgewählten Vorlage werden übernommen. In diesem Schritt wird die Vorlage unverändert hinzugefügt.",
        )
      : selection.mode === "assigned"
        ? t(
            "Pflegen Sie hier die Daten, die später in der Bestandsaufnahme, Kalkulation und optional im Katalog verwendet werden.",
          )
        : t(
            "Erfassen Sie hier direkt einen neuen materialbezogenen Zusatz. Die Eingaben werden beim Speichern dem Monitor hinzugefügt.",
          );

  const dialogDescription =
    selection.mode === "assigned"
      ? t(
          "Bearbeiten Sie den ausgewählten Material-Zusatz. Änderungen werden erst beim Speichern übernommen.",
        )
      : t(
          "Vorhandene Zusätze können gesucht und als Vorlage übernommen werden. Für neue Zusätze werden nur Name, Preis und Katalogtext gepflegt. Änderungen werden erst beim Speichern übernommen.",
        );

  const executePrimaryAction = () => {
    setCloseOnSuccessfulSave(true);
    setPersistenceState(PersistenceState.SAVING);

    const currentSelection = selectionRef.current;
    const currentDraftAdditionals = draftAdditionalsRef.current;
    const currentSelectedAdditional =
      currentSelection.mode === "assigned"
        ? currentDraftAdditionals.find(
            (additional) => additional.id === currentSelection.additionalId,
          )
        : currentSelection.additional;

    if (!currentSelectedAdditional) {
      persistAdditionals(currentDraftAdditionals);
      return;
    }

    const selectedAndDrafts =
      currentSelection.mode === "assigned"
        ? currentDraftAdditionals
        : [...currentDraftAdditionals, currentSelectedAdditional];
    if (
      monitorType === "IOT_MONITOR" &&
      selectedAndDrafts.some((additional) =>
        hasInvalidRentingFeeRows(rentingFeesToRows(additional.rentingFees)),
      )
    ) {
      toast.error(
        t(
          "Bitte prüfen Sie die Mietpreise. Laufzeiten dürfen nicht doppelt vorkommen.",
        ),
      );
      setPersistenceState(PersistenceState.ERROR);
      return;
    }

    if (currentSelection.mode === "create") {
      if (isAdditionalEmpty(currentSelection.additional)) {
        persistAdditionals(currentDraftAdditionals);
        return;
      }
      persistAdditionals([
        ...currentDraftAdditionals,
        sanitizeAdditionalForMonitorType(
          currentSelection.additional,
          monitorType,
        ),
      ]);
      return;
    }

    if (currentSelection.mode === "library") {
      persistAdditionals([
        ...currentDraftAdditionals,
        sanitizeAdditionalForMonitorType(
          currentSelection.additional,
          monitorType,
        ),
      ]);
      return;
    }

    persistAdditionals(
      currentDraftAdditionals.map((additional) =>
        sanitizeAdditionalForMonitorType(additional, monitorType),
      ),
    );
  };

  const dialogTitle = (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div>{t("Material-Zusätze konfigurieren")}</div>
        <div className="text-muted-foreground mt-1 text-sm font-normal">
          {monitorName || t("Unbenannter Monitor")}
        </div>
      </div>
      <PersistenceStateUsingContext className="mt-0.5 shrink-0 text-xs" />
    </div>
  );

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="text-sm font-semibold">{t("Material-Zusätze")}</div>
            <div className="text-muted-foreground text-sm">
              {t(
                "Optionale materialbezogene Zusätze wie Gehäuse. Auswahl und Berechnungsmodus erfolgen später in der Bestandsaufnahme.",
              )}
            </div>
          </div>
          <DialogTooltipButton
            open={isOpen}
            onOpenChange={handleOpenChange}
            tooltip={t(
              "Öffnet die Konfiguration für optionale materialbezogene Zusätze wie Gehäuse. Hier können bestehende Vorlagen übernommen oder neue Zusätze angelegt werden.",
            )}
            dialogTitle={dialogTitle}
            icon={<Plus className="mr-2 h-4 w-4" />}
            label={t("Material-Zusätze hinzufügen")}
            buttonProps={{
              variant: "outline",
              size: "default",
            }}
            tooltipContentProps={{
              className: baseDataTooltipContentClassName,
            }}
            dialogContentProps={{
              className:
                "h-[min(88vh,960px)] w-[min(96vw,1280px)] max-w-[1280px] gap-0 overflow-hidden p-0 sm:max-w-[1280px] [&_[data-slot=dialog-header]]:px-8 [&_[data-slot=dialog-header]]:pt-6 [&_[data-slot=dialog-header]]:pb-3",
            }}
            content={
              <>
                <div className="border-b px-8 pb-6">
                  <DialogDescription className="space-y-2 text-sm leading-6">
                    <span className="block">
                      {t(
                        "Material-Zusätze erweitern einen Monitor nur um Materialkosten. Sie erzeugen keine zusätzlichen Installations, Wartungs oder Servicekosten.",
                      )}
                    </span>
                    <span className="block">{dialogDescription}</span>
                  </DialogDescription>
                </div>

                <div className="flex h-full min-h-0 flex-col">
                  <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
                    {saveState.error ? (
                      <div className="mb-4">
                        <RTKQueryErrorAlert error={saveState.error} />
                      </div>
                    ) : null}

                    <div className="space-y-6">
                      {showLibrarySearch && isLibraryLoading ? (
                        <div className="flex max-w-2xl items-center gap-2 rounded-md border p-4 text-sm">
                          <Loader className="h-4 w-4 animate-spin" />
                          {t("Zusätze werden geladen")}
                        </div>
                      ) : showLibrarySearch ? (
                        <AdditionalLibrarySearch
                          items={availableLibraryItems}
                          disabled={isSaving}
                          onSelect={handleAddFromLibrary}
                        />
                      ) : null}

                      <div>
                        <div className="space-y-5">
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-1">
                              <div className="text-sm font-medium">
                                {editorTitle}
                              </div>
                              <div className="text-muted-foreground text-sm leading-5">
                                {editorDescription}
                              </div>
                            </div>
                            {selection.mode !== "create" ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  updateSelection({
                                    mode: "create",
                                    additional: createNewAdditional(),
                                  })
                                }
                                disabled={isSaving}
                              >
                                <Plus className="mr-2 h-4 w-4" />
                                {t("Neu erfassen")}
                              </Button>
                            ) : null}
                          </div>
                          <MonitorAdditionalEditForm
                            additional={selectedAdditional}
                            monitorType={monitorType}
                            disabled={isSaving}
                            readOnly={selection.mode === "library"}
                            onChange={handleChangeSelectedAdditional}
                            imageSlot={
                              <MonitorAdditionalImageSlot
                                additional={
                                  selectedAdditional &&
                                  selectedPersistedAdditional
                                    ? {
                                        ...selectedPersistedAdditional,
                                        name: selectedAdditional.name,
                                        description:
                                          selectedAdditional.description,
                                        basePrice: selectedAdditional.basePrice,
                                        rentingFees:
                                          selectedAdditional.rentingFees,
                                        config: selectedAdditional.config,
                                        createdAt: selectedAdditional.createdAt,
                                        updatedAt: selectedAdditional.updatedAt,
                                      }
                                    : undefined
                                }
                                mode={mode}
                                disabled={isSaving}
                                readOnly={selection.mode === "library"}
                              />
                            }
                          />
                        </div>
                      </div>

                      <AdditionalDraftList
                        additionals={draftAdditionals}
                        selectedId={selectedAdditionalId}
                        disabled={isSaving}
                        onSelect={(additionalId) =>
                          updateSelection({ mode: "assigned", additionalId })
                        }
                        onDelete={handleDeleteDraftAdditional}
                        onEditRentingFees={(additionalId) =>
                          updateSelection({ mode: "assigned", additionalId })
                        }
                      />
                    </div>
                  </div>

                  <DialogFooter className="border-t px-8 py-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleOpenChange(false)}
                      disabled={isSaving}
                    >
                      {t("Abbrechen")}
                    </Button>
                    <Button
                      type="button"
                      onClick={executePrimaryAction}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="mr-2 h-4 w-4" />
                      )}
                      {primaryActionLabel}
                    </Button>
                  </DialogFooter>
                </div>
              </>
            }
          />
        </div>

        <AdditionalSummary
          additionals={additionals}
          libraryItems={libraryItems}
          disabled={isSaving}
          onEdit={openEditorForAdditional}
          onDelete={deletePersistedAdditional}
          onPriceChange={updatePersistedAdditionalPrice}
        />
      </div>
    </>
  );
}
